import { GameState } from './GameState.js';
import type { MoveValidator } from './MoveValidator.js';
import { isPromotionSquare } from './path.js';
import { sameSquare, type Move, type Piece, type PieceKind, type Square } from './types.js';

export type TurnPhase = 'idle' | 'selected' | 'chaining' | 'done';

export type StepResult =
  | { readonly kind: 'invalid' }
  | { readonly kind: 'progress' }
  | { readonly kind: 'complete'; readonly move: Move };

/**
 * Drives a single player's turn from selection through a (possibly chained)
 * capture all the way to a committable `Move`. Holds the pre-turn snapshot for
 * revert and tracks how far the player has progressed into one of the maximal
 * chains returned by `MoveValidator.legalMoves`.
 *
 * The chain-capture rule is enforced by *narrowing* candidates as the player
 * picks landing squares. Russian Shashki's enumerate-all-maximal-chains output
 * makes this clean: at any time, `_chain` is the set of legal continuations
 * consistent with what's been played; `nextDestinations()` is the union of the
 * next path step across those candidates.
 */
export class TurnState {
  private _snapshot: GameState;
  private _phase: TurnPhase = 'idle';
  private _origin: Square | null = null;
  private _stepIndex = 0;
  private _all: readonly Move[];
  private _chain: readonly Move[];
  private _completed: Move | null = null;

  constructor(state: GameState, private readonly validator: MoveValidator) {
    this._snapshot = state;
    this._all = validator.legalMoves(state);
    this._chain = this._all;
  }

  /** Replace the underlying state — call after applying a committed move. */
  reset(state: GameState): void {
    this._snapshot = state;
    this._phase = 'idle';
    this._origin = null;
    this._stepIndex = 0;
    this._all = this.validator.legalMoves(state);
    this._chain = this._all;
    this._completed = null;
  }

  get phase(): TurnPhase {
    return this._phase;
  }

  /** State at the START of this turn (pre-move). */
  get snapshot(): GameState {
    return this._snapshot;
  }

  get origin(): Square | null {
    return this._origin;
  }

  /** Where the moving piece currently sits (origin, then each chain landing). */
  get currentSquare(): Square | null {
    if (!this._origin) return null;
    if (this._stepIndex === 0) return this._origin;
    const candidate = this._chain[0];
    if (!candidate) return null;
    return candidate.path[this._stepIndex - 1] ?? null;
  }

  /** Captures consumed so far in the chain (chain order). */
  get appliedCaptures(): readonly Square[] {
    if (this._stepIndex === 0 || this._chain.length === 0) return [];
    const m = this._chain[0];
    if (!m) return [];
    return m.captures.slice(0, this._stepIndex);
  }

  /** Landings traversed so far. */
  get appliedPath(): readonly Square[] {
    if (this._stepIndex === 0 || this._chain.length === 0) return [];
    const m = this._chain[0];
    if (!m) return [];
    return m.path.slice(0, this._stepIndex);
  }

  /** The committed move — only set in phase 'done'. */
  get completed(): Move | null {
    return this._completed;
  }

  hasAnyMove(): boolean {
    return this._all.length > 0;
  }

  /** Sources of every legal move available right now. */
  legalSources(): readonly Square[] {
    return dedupSquares(this._all.map((m) => m.from));
  }

  /**
   * Pieces with a mandatory capture in the current position. Empty when the
   * only legal moves are quiet — so the overlay doesn't pulse needlessly.
   */
  mandatorySources(): readonly Square[] {
    const first = this._all[0];
    if (!first || first.captures.length === 0) return [];
    return this.legalSources();
  }

  /** Where can the moving piece go right now? */
  nextDestinations(): readonly Square[] {
    if (this._phase === 'idle' || this._phase === 'done') return [];
    const out: Square[] = [];
    const seen = new Set<string>();
    for (const m of this._chain) {
      const next = m.path[this._stepIndex];
      if (!next) continue;
      const k = squareKey(next);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(next);
    }
    return out;
  }

  /** Opponent pieces removed if the user steps to `to` next. */
  captureTargetsFor(to: Square): readonly Square[] {
    if (this._phase === 'idle' || this._phase === 'done') return [];
    const out: Square[] = [];
    const seen = new Set<string>();
    for (const m of this._chain) {
      const next = m.path[this._stepIndex];
      if (!next || !sameSquare(next, to)) continue;
      const cap = m.captures[this._stepIndex];
      if (!cap) continue;
      const k = squareKey(cap);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(cap);
    }
    return out;
  }

  canSelect(sq: Square): boolean {
    if (this._phase !== 'idle' && this._phase !== 'selected') return false;
    return this._all.some((m) => sameSquare(m.from, sq));
  }

  /** Pick a source. Switching sources is only allowed before the first step. */
  select(sq: Square): boolean {
    if (this._phase === 'chaining' || this._phase === 'done') return false;
    const filtered = this._all.filter((m) => sameSquare(m.from, sq));
    if (filtered.length === 0) return false;
    this._phase = 'selected';
    this._origin = sq;
    this._stepIndex = 0;
    this._chain = filtered;
    return true;
  }

  /** Clear selection (only in idle/selected — chains can't be abandoned). */
  deselect(): void {
    if (this._phase === 'chaining' || this._phase === 'done') return;
    this._phase = 'idle';
    this._origin = null;
    this._stepIndex = 0;
    this._chain = this._all;
  }

  /** Try to step to `to`. Returns progress / complete / invalid. */
  step(to: Square): StepResult {
    if (this._phase !== 'selected' && this._phase !== 'chaining') {
      return { kind: 'invalid' };
    }
    const consistent = this._chain.filter((m) => {
      const next = m.path[this._stepIndex];
      return next !== undefined && sameSquare(next, to);
    });
    if (consistent.length === 0) return { kind: 'invalid' };

    const nextStepIndex = this._stepIndex + 1;
    const hasContinuation = consistent.some((m) => m.path.length > nextStepIndex);

    if (!hasContinuation) {
      // CaptureSearch only emits maximal chains, so for any path prefix at
      // most one candidate terminates there. Russian rules forbid stopping a
      // chain voluntarily, so terminal-and-continuation can't coexist.
      const terminal = consistent.find((m) => m.path.length === nextStepIndex);
      if (!terminal) return { kind: 'invalid' };
      this._chain = [terminal];
      this._stepIndex = nextStepIndex;
      this._phase = 'done';
      this._completed = terminal;
      return { kind: 'complete', move: terminal };
    }

    this._chain = consistent;
    this._stepIndex = nextStepIndex;
    this._phase = 'chaining';
    return { kind: 'progress' };
  }

  /** True once the chain's path has crossed the mover's king row. */
  hasPromotedMidChain(): boolean {
    if (!this._origin || this._stepIndex === 0) return false;
    const mover = this._snapshot.pieceAt(this._origin);
    if (!mover || mover.kind !== 'man') return false;
    for (const sq of this.appliedPath) {
      if (isPromotionSquare(sq, mover.color, this._snapshot.boardSize)) return true;
    }
    return false;
  }

  /**
   * The kind to render for the moving piece *right now*, accounting for any
   * mid-chain promotion. Returns null if no piece is selected.
   */
  currentPieceKind(): PieceKind | null {
    if (!this._origin) return null;
    const mover = this._snapshot.pieceAt(this._origin);
    if (!mover) return null;
    if (mover.kind === 'king') return 'king';
    return this.hasPromotedMidChain() ? 'king' : 'man';
  }

  /**
   * Snapshot-style GameState with the partial chain applied — captured pieces
   * removed and the mover relocated to the current chain square. Used for
   * rendering during chain progress; the real GameState only mutates when the
   * chain commits.
   */
  previewState(): GameState {
    if (this._stepIndex === 0 || !this._origin) return this._snapshot;
    const mover = this._snapshot.pieceAt(this._origin);
    const here = this.currentSquare;
    if (!mover || !here) return this._snapshot;
    const removed = new Set<string>(this.appliedCaptures.map(squareKey));
    const movingPiece: Piece = {
      color: mover.color,
      kind: this.hasPromotedMidChain() ? 'king' : mover.kind,
    };
    const pieces: { square: Square; piece: Piece }[] = [];
    for (const { square, piece } of this._snapshot.pieces()) {
      if (sameSquare(square, this._origin)) continue;
      if (removed.has(squareKey(square))) continue;
      pieces.push({ square, piece });
    }
    pieces.push({ square: here, piece: movingPiece });
    return GameState.custom(
      this._snapshot.ruleSet,
      pieces,
      this._snapshot.currentPlayer,
      {
        moveCount: this._snapshot.moveCount,
        quietMoveCount: this._snapshot.quietMoveCount,
        positionHistory: this._snapshot.positionHistory,
      },
    );
  }
}

function squareKey(sq: Square): string {
  return `${sq.row},${sq.col}`;
}

function dedupSquares(items: Iterable<Square>): Square[] {
  const seen = new Set<string>();
  const out: Square[] = [];
  for (const sq of items) {
    const k = squareKey(sq);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(sq);
  }
  return out;
}
